import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { LearningModule, UserProgress, Project, PersonalityTrait, TaxonomyLevel } from '@/types/supabase';
import { Link } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { BadgeCheck, Clock, BookOpen, Calendar, Brain, FileCode, Target } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';

const Dashboard = () => {
  const { user, profile } = useAuth();
  const [modules, setModules] = useState<LearningModule[]>([]);
  const [progress, setProgress] = useState<UserProgress[]>([]);
  const [upcomingOfficeHours, setUpcomingOfficeHours] = useState([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [personalityTraits, setPersonalityTraits] = useState<PersonalityTrait[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const { toast } = useToast();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch learning modules
        const { data: modulesData, error: modulesError } = await supabase
          .from('learning_modules')
          .select('*')
          .order('order_position', { ascending: true });
        
        if (modulesError) throw modulesError;
        
        // Properly type cast the modules data
        const typedModules = modulesData.map(module => ({
          ...module,
          phase: module.phase as LearningModule['phase'],
          track: module.track as LearningModule['track'],
          taxonomy_level: module.taxonomy_level as TaxonomyLevel[] || []
        }));
        
        setModules(typedModules);
        
        // Fetch user progress
        if (user) {
          const { data: progressData, error: progressError } = await supabase
            .from('user_progress')
            .select('*')
            .eq('user_id', user.id);
          
          if (progressError) throw progressError;
          setProgress(progressData);
          
          // Fetch upcoming office hours
          const { data: officeHoursData, error: officeHoursError } = await supabase
            .from('office_hours')
            .select('*')
            .gt('start_time', new Date().toISOString())
            .order('start_time', { ascending: true })
            .limit(3);
          
          if (officeHoursError) throw officeHoursError;
          setUpcomingOfficeHours(officeHoursData);
          
          // Fetch user projects
          const { data: projectsData, error: projectsError } = await supabase
            .from('projects')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });
          
          if (projectsError) throw projectsError;
          setProjects(projectsData);
          
          // Fetch personality traits
          const { data: traitsData, error: traitsError } = await supabase
            .from('personality_traits')
            .select('*')
            .eq('user_id', user.id)
            .order('score', { ascending: false });
          
          if (traitsError) throw traitsError;
          setPersonalityTraits(traitsData);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast({
          title: "Error loading dashboard",
          description: "There was a problem loading your dashboard data.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [user, toast]);

  const getModuleProgress = (moduleId: string) => {
    const moduleProgress = progress.find(p => p.module_id === moduleId);
    return moduleProgress ? moduleProgress.completion_percentage : 0;
  };

  const getOverallProgress = () => {
    if (modules.length === 0) return 0;
    
    const completedModules = progress.filter(p => p.completion_percentage === 100).length;
    return (completedModules / modules.length) * 100;
  };

  const getNextModule = () => {
    // Find the first module with less than 100% progress
    if (modules.length === 0) return null;
    
    for (const module of modules) {
      const moduleProgress = getModuleProgress(module.id);
      if (moduleProgress < 100) {
        return module;
      }
    }
    
    // If all modules are complete, return the last one
    return modules[modules.length - 1];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const nextModule = getNextModule();

  return (
    <div className="space-y-6">
      <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="modules">Learning Modules</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="personality">Personality Profile</TabsTrigger>
          <TabsTrigger value="office-hours">Office Hours</TabsTrigger>
        </TabsList>
        
        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Welcome Card */}
            <Card className="flex-1">
              <CardHeader>
                <CardTitle>Welcome back, {profile?.first_name || 'Student'}!</CardTitle>
                <CardDescription>
                  Track your bootcamp progress and continue your learning journey.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">Overall Progress</span>
                      <span className="text-sm font-medium">{Math.round(getOverallProgress())}%</span>
                    </div>
                    <Progress value={getOverallProgress()} className="h-2" />
                  </div>
                  
                  {nextModule && (
                    <div>
                      <h3 className="text-base font-medium mb-2">Continue Learning</h3>
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <div>
                              <h4 className="font-medium">{nextModule.title}</h4>
                              <p className="text-sm text-muted-foreground">{getModuleProgress(nextModule.id)}% complete</p>
                            </div>
                            <Button asChild>
                              <Link to={`/modules/${nextModule.id}`}>
                                Continue
                              </Link>
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Stats Card */}
            <Card className="w-full md:w-80">
              <CardHeader>
                <CardTitle>Your Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center">
                  <BadgeCheck className="h-5 w-5 mr-2 text-green-500" />
                  <span>
                    <strong>{progress.filter(p => p.completion_percentage === 100).length}</strong> modules completed
                  </span>
                </div>
                <div className="flex items-center">
                  <Clock className="h-5 w-5 mr-2 text-blue-500" />
                  <span>
                    <strong>{progress.length}</strong> modules in progress
                  </span>
                </div>
                <div className="flex items-center">
                  <BookOpen className="h-5 w-5 mr-2 text-purple-500" />
                  <span>
                    <strong>{modules.length - progress.length}</strong> modules not started
                  </span>
                </div>
                <div className="flex items-center">
                  <FileCode className="h-5 w-5 mr-2 text-amber-500" />
                  <span>
                    <strong>{projects.length}</strong> projects submitted
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Learning Modules Tab */}
        <TabsContent value="modules" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Your Learning Path</CardTitle>
              <CardDescription>
                Track your progress through all bootcamp modules
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {modules.map((module) => (
                  <div key={module.id} className="border rounded-lg p-4">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                      <div className="space-y-1">
                        <h3 className="font-medium">{module.title}</h3>
                        <p className="text-sm text-muted-foreground">{module.description}</p>
                        <div className="flex items-center mt-2">
                          <span className="text-xs font-medium bg-secondary text-secondary-foreground px-2 py-1 rounded-full">
                            {module.difficulty_level}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className="text-sm font-medium">{getModuleProgress(module.id)}% complete</span>
                        <Button variant="outline" asChild>
                          <Link to={`/modules/${module.id}`}>
                            {getModuleProgress(module.id) > 0 ? 'Continue' : 'Start'}
                          </Link>
                        </Button>
                      </div>
                    </div>
                    <Progress value={getModuleProgress(module.id)} className="h-2 mt-4" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Projects Tab */}
        <TabsContent value="projects" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Your Projects</CardTitle>
              <CardDescription>
                View and manage your project submissions
              </CardDescription>
            </CardHeader>
            <CardContent>
              {projects.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Project Title</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Submission Date</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {projects.map((project) => (
                      <TableRow key={project.id}>
                        <TableCell className="font-medium">{project.title}</TableCell>
                        <TableCell>{project.description?.substring(0, 50)}{project.description && project.description.length > 50 ? '...' : ''}</TableCell>
                        <TableCell>{new Date(project.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>{project.score !== null ? `${project.score}/100` : 'Pending'}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            project.score !== null ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                          }`}>
                            {project.score !== null ? 'Graded' : 'Submitted'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm" asChild>
                            <Link to={`/projects/${project.id}`}>View</Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <FileCode className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                  <p>You haven't submitted any projects yet.</p>
                  <Button variant="outline" className="mt-4" asChild>
                    <Link to="/projects/new">Start a New Project</Link>
                  </Button>
                </div>
              )}
            </CardContent>
            {projects.length > 0 && (
              <CardFooter>
                <Button variant="outline" className="w-full" asChild>
                  <Link to="/projects/new">Start a New Project</Link>
                </Button>
              </CardFooter>
            )}
          </Card>
        </TabsContent>
        
        {/* Personality Profile Tab */}
        <TabsContent value="personality" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Brain className="h-6 w-6 mr-2 text-purple-500" />
                Your Personality Profile
              </CardTitle>
              <CardDescription>
                Traits and characteristics that shape your learning style
              </CardDescription>
            </CardHeader>
            <CardContent>
              {personalityTraits.length > 0 ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {personalityTraits.map((trait) => (
                      <div key={trait.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="font-medium capitalize">{trait.trait}</h3>
                          <span className="text-sm font-bold">{trait.score}/10</span>
                        </div>
                        <Progress value={trait.score * 10} className="h-2" />
                        <p className="text-sm text-muted-foreground mt-2">
                          {getTraitDescription(trait.trait)}
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-secondary/30 p-4 rounded-lg">
                    <h3 className="font-medium flex items-center mb-2">
                      <Target className="h-5 w-5 mr-2 text-blue-500" />
                      Learning Style Insights
                    </h3>
                    <p className="text-sm">
                      Based on your personality traits, you tend to learn best through 
                      {personalityTraits.some(t => t.trait === 'analytical' && t.score > 7) ? ' structured problem-solving and ' : ' '}
                      {personalityTraits.some(t => t.trait === 'creative' && t.score > 7) ? 'creative exploration and ' : ''}
                      {personalityTraits.some(t => t.trait === 'collaborative' && t.score > 7) ? 'collaborative projects' : 'independent study'}. 
                      Consider focusing on learning activities that match these strengths.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <Brain className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                  <p>Your personality profile hasn't been analyzed yet.</p>
                  <Button variant="outline" className="mt-4" asChild>
                    <Link to="/personality-assessment">Take Personality Assessment</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Office Hours Tab */}
        <TabsContent value="office-hours" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>All Office Hours Sessions</CardTitle>
                <CardDescription>
                  Join office hours to get help with your projects
                </CardDescription>
              </div>
              <Calendar className="h-5 w-5 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {upcomingOfficeHours.length > 0 ? (
                <div className="space-y-4">
                  {upcomingOfficeHours.map((session: any) => (
                    <div key={session.id} className="border rounded-lg p-4">
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div className="space-y-1">
                          <h3 className="font-medium">{session.title}</h3>
                          <p className="text-sm text-muted-foreground">{session.description}</p>
                          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-2">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1 text-muted-foreground" />
                              <span className="text-sm">
                                {new Date(session.start_time).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="flex items-center">
                              <Clock className="h-4 w-4 mr-1 text-muted-foreground" />
                              <span className="text-sm">
                                {new Date(session.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - 
                                {new Date(session.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          </div>
                        </div>
                        <Button variant="outline" asChild>
                          <Link to={`/office-hours/${session.id}`}>
                            Reserve Spot
                          </Link>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  No upcoming office hours scheduled.
                </div>
              )}
            </CardContent>
            <CardFooter>
              <Button variant="outline" className="w-full" asChild>
                <Link to="/office-hours">View Full Calendar</Link>
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Helper function to get descriptions for personality traits
const getTraitDescription = (trait: string) => {
  const descriptions: Record<string, string> = {
    analytical: "You excel at logical problem-solving and detailed analysis.",
    creative: "You thrive when generating new ideas and thinking outside the box.",
    collaborative: "You work well in team settings and benefit from group discussions.",
    independent: "You prefer self-directed learning and autonomous projects.",
    adaptive: "You easily adjust to new situations and changing requirements.",
    persistent: "You demonstrate determination in overcoming obstacles.",
    methodical: "You approach problems in a structured, organized way.",
    intuitive: "You often rely on instinct and quick pattern recognition.",
  };
  
  return descriptions[trait.toLowerCase()] || "This trait influences your approach to learning and problem-solving.";
};

export default Dashboard;
